import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useSiteAssets } from "@/lib/siteAssets";
import { imageUrl } from "@/components/ImageUpload";
import { toast } from "sonner";

export default function Login() {
    const { signIn } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const [form, setForm] = useState({ email: "", password: "" });
    const [busy, setBusy] = useState(false);
    const assets = useSiteAssets();

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await signIn(form.email, form.password);
            toast.success("Welcome back!");
            const next = params.get("next");
            if (next) nav(next);
            else if (res.role === "admin") nav("/admin");
            else if (res.role === "host_company") nav("/host");
            else nav("/vendor");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid md:grid-cols-2">
            <div className="relative hidden md:block">
                <img src={imageUrl(assets.login_background)} className="absolute inset-0 w-full h-full object-cover" alt="" />
                <div className="absolute inset-0 bg-brand-forest/70" />
                <div className="relative h-full flex flex-col justify-between p-12 text-white">
                    <Link to="/" className="font-heading text-xl font-extrabold">HealthFair<span className="text-brand-coral">X</span></Link>
                    <div>
                        <h2 className="font-heading text-4xl font-extrabold leading-tight">Calmer fairs.<br />Happier teams.</h2>
                        <p className="mt-4 text-white/80 max-w-md">The marketplace for corporate wellness events.</p>
                    </div>
                    <div className="text-xs text-white/60">© {new Date().getFullYear()} HealthFairX</div>
                </div>
            </div>
            <div className="flex items-center justify-center p-8 md:p-16">
                <div className="w-full max-w-md">
                    <Link to="/" className="md:hidden font-heading text-xl font-extrabold block mb-8">HealthFair<span className="text-brand-coral">X</span></Link>
                    <h1 className="font-heading text-3xl font-extrabold">Welcome back</h1>
                    <p className="text-brand-mute mt-2 text-sm">Sign in to your HealthFairX account.</p>

                    <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
                        <div>
                            <label className="label-base">Email</label>
                            <input data-testid="login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-base" placeholder="you@company.com" />
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <label className="label-base">Password</label>
                                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-brand-forest font-semibold hover:underline">Forgot password?</Link>
                            </div>
                            <input data-testid="login-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-base" placeholder="••••••••" />
                        </div>
                        <button data-testid="login-submit" disabled={busy} type="submit" className="pill btn-coral w-full h-11 font-semibold disabled:opacity-50">
                            {busy ? "Signing in…" : "Sign in"}
                        </button>
                    </form>
                    <p className="mt-6 text-sm text-brand-mute">New here? <Link to="/register" data-testid="login-go-register" className="text-brand-forest font-semibold hover:underline">Create an account</Link></p>
                </div>
            </div>
        </div>
    );
}
